"use client";

import { useState } from "react";
import { Mail, MessageSquare, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function QuestionsAndContactInformation() {
  return <section className="relative bg-background py-12 overflow-hidden">
      
    </section>;
}
function ContentSection() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: summaryRef,
    isVisible: summaryVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <>
      {/* Header with icon items and image */}
      <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12" style={{
      opacity: headerVisible ? 1 : 0,
      transform: headerVisible ? "translateY(0)" : "translateY(20px)",
      transition: "opacity 600ms ease-out, transform 600ms ease-out"
    }}>
        {/* Left side: Text and icons */}
        <div className="flex flex-col justify-center space-y-6">
          
          <div className="space-y-4">
            <IconItem icon={<Phone className="size-6 text-primary" />} text="Call us for immediate assistance" />
            <IconItem icon={<Mail className="size-6 text-primary" />} text="Email us your questions anytime" />
            <IconItem icon={<MessageSquare className="size-6 text-primary" />} text="Chat with our friendly team" />
          </div>
        </div>

        {/* Right side: Image */}
        <div className="relative flex items-center justify-center lg:justify-end">
          
        </div>
      </div>

      {/* Contact Form */}
      <div ref={formRef} className="max-w-2xl mx-auto mb-12" style={{
      opacity: formVisible ? 1 : 0,
      transform: formVisible ? "translateY(0)" : "translateY(40px)",
      transition: "opacity 700ms ease-out, transform 700ms ease-out"
    }}>
        <div className="card p-6 shadow-medium hover:shadow-strong transition-shadow duration-300">
          <ContactForm />
        </div>
      </div>

      {/* Summary text */}
      <div ref={summaryRef} className="max-w-3xl mx-auto text-center" style={{
      opacity: summaryVisible ? 1 : 0,
      transform: summaryVisible ? "translateY(0)" : "translateY(20px)",
      transition: "opacity 600ms ease-out, transform 600ms ease-out"
    }}>
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
          We're here to help answer any questions you may have. Reach out to us
          and we'll respond as soon as we can. Whether it's a quick question or
          you need more detailed information, our team is ready to assist you.
        </p>
      </div>
    </>;
}
function IconItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="flex items-center gap-4">
      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary-light-bg flex items-center justify-center">
        {icon}
      </div>
      <p className="text-foreground text-base font-medium">{text}</p>
    </div>;
}
interface ContactFormData {
  name: string;
  email: string;
  message: string;
}
function ContactForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      email: "",
      message: ""
    }
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: {
      isSubmitting
    }
  } = form;
  const onSubmit = async (data: ContactFormData) => {
    try {
      setSubmitError(null);

      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In a real application, you would send the data to your backend here
      console.log("Form submitted:", data);
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError("An error occurred while sending your message. Please try again.");
    }
  };
  const resetForm = () => {
    setIsSubmitted(false);
    setSubmitError(null);
    reset();
  };
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-secondary-light-bg border border-secondary rounded-lg text-center">
        <p className="text-foreground text-lg font-semibold mb-4">
          Thank you! We'll respond to your message soon.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" {...field} className="focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="focus:border-primary focus:ring-primary/30 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us how we can help..." {...field} className="focus:border-primary focus:ring-primary/30 transition-all duration-200 min-h-32" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent-hover hover:scale-105 active:scale-100 transition-transform duration-200">
          {isSubmitting ? <>
              <span className="inline-block animate-spin mr-2">⏳</span>
              Sending...
            </> : "Send Message"}
        </Button>
      </form>
    </Form>;
}